import { create } from '@bufbuild/protobuf';
import { createClient } from '@connectrpc/connect';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import {
  CheckDeploymentDeleteBlockersRequestSchema,
  CreateDeploymentRequestSchema,
  DeleteDeploymentRequestSchema,
  DeleteMode,
  type DeploymentModel,
  DeploymentService,
  type DeploymentStatus,
  type DeploymentType,
  GetBootstrapTemplateRequestSchema,
  GetDeploymentRequestSchema,
  GetLastDeploymentWorkflowRunRequestSchema,
  ListAccessibleDeploymentsRequestSchema,
  ListControlPlanesRequestSchema,
  ListDeploymentInfrasRequestSchema,
  ListDeploymentsRequestSchema,
  StreamDeploymentEventsRequestSchema,
  TriggerTenantMigrationRequestSchema,
} from '../gen/deployments/v1/deployments_pb';
import { createApiTransport, createAuthApiTransport } from '../transport';
import { isNonRetriable, streamSleep } from './streamingUtils';
import type { AccessRuleInputData } from './useUserService';

export { DeleteMode };

function transport(token?: string | null) {
  return token ? createAuthApiTransport(token) : createApiTransport();
}

export interface ListDeploymentsParams {
  search?: string;
  page?: number;
  pageSize?: number;
  deploymentTypeFilter?: DeploymentType;
  statusFilter?: DeploymentStatus;
}

/**
 * Mutation hook to create a new deployment for a tenant.
 * On success, invalidates the deployments list cache.
 *
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @returns Mutation result for creating a deployment
 */
export function useCreateDeployment(token?: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      tenantId: string;
      displayName: string;
      slug: string;
      deploymentType: DeploymentType;
      deploymentModel: DeploymentModel;
      region: string;
    }) => {
      const client = createClient(DeploymentService, transport(token));
      const request = create(CreateDeploymentRequestSchema, {
        tenantId: input.tenantId,
        displayName: input.displayName,
        slug: input.slug,
        deploymentType: input.deploymentType,
        deploymentModel: input.deploymentModel,
        region: input.region,
      });
      return await client.createDeployment(request);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['deployments', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['deployments', 'list', variables.tenantId] });
    },
  });
}

/**
 * Hook to list deployments with optional server-side search, filter, and pagination.
 *
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @param tenantId - Customer tenant ID to filter deployments by
 * @param parentTenantId - Partner tenant ID to filter by (returns all child tenant deployments)
 * @param params - Optional search, filter, and pagination params
 * @param enabled - Whether the query should execute (default: true)
 * @returns Query result with list of deployments and totalCount
 */
export function useListDeployments(
  token?: string | null,
  tenantId?: string,
  parentTenantId?: string,
  params?: ListDeploymentsParams,
  enabled = true,
) {
  return useQuery({
    queryKey: [
      'deployments',
      'list',
      tenantId ?? '',
      parentTenantId ?? '',
      !!token,
      params?.search ?? '',
      params?.page ?? 1,
      params?.pageSize ?? 20,
      params?.deploymentTypeFilter ?? 0,
      params?.statusFilter ?? 0,
    ],
    queryFn: async () => {
      const client = createClient(DeploymentService, transport(token));
      const request = create(ListDeploymentsRequestSchema, {
        tenantId: tenantId ?? '',
        parentTenantId: parentTenantId ?? '',
        search: params?.search ?? '',
        page: params?.page ?? 1,
        pageSize: params?.pageSize ?? 20,
        deploymentTypeFilter: params?.deploymentTypeFilter,
        statusFilter: params?.statusFilter,
      });
      return await client.listDeployments(request);
    },
    enabled: !!token && enabled,
  });
}

export interface ListAccessibleDeploymentsParams {
  search?: string;
  page?: number;
  pageSize?: number;
  roleRules?: AccessRuleInputData[];
  userRules?: AccessRuleInputData[];
}

/**
 * Hook to list deployments visible under a candidate (not persisted)
 * access-rule scope passed in the request itself — the access wizard's
 * resource-picker / live-preview replacement for the retired
 * PreviewAccessRuleMatch. Unlike useListDeployments, this never consults the
 * caller's own access_rules; roleRules/userRules entirely decide visibility.
 *
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @param tenantId - Filter by direct tenant ID. Mutually exclusive with parentTenantId.
 * @param parentTenantId - Filter by parent tenant ID.
 * @param params - Search/pagination and the candidate rule envelopes
 * @param enabled - Set false to skip fetching (e.g. while the condition being previewed is incomplete)
 * @returns Query result with list of deployments and totalCount
 */
export function useListAccessibleDeployments(
  token?: string | null,
  tenantId?: string,
  parentTenantId?: string,
  params?: ListAccessibleDeploymentsParams,
  enabled = true,
) {
  return useQuery({
    queryKey: [
      'deployments',
      'accessible',
      tenantId ?? '',
      parentTenantId ?? '',
      !!token,
      params?.search ?? '',
      params?.page ?? 1,
      params?.pageSize ?? 20,
      params?.roleRules ?? [],
      params?.userRules ?? [],
    ],
    queryFn: async () => {
      const client = createClient(DeploymentService, transport(token));
      const request = create(ListAccessibleDeploymentsRequestSchema, {
        tenantId: tenantId ?? '',
        parentTenantId: parentTenantId ?? '',
        search: params?.search ?? '',
        page: params?.page ?? 1,
        pageSize: params?.pageSize ?? 20,
        roleRules: (params?.roleRules ?? []).map((r) => ({ ...r, effect: r.effect ?? 'allow' })),
        userRules: (params?.userRules ?? []).map((r) => ({ ...r, effect: r.effect ?? 'allow' })),
      });
      return await client.listAccessibleDeployments(request);
    },
    enabled: !!token && enabled,
  });
}

/**
 * Hook to retrieve a single deployment by ID.
 *
 * @param id - The deployment ID to retrieve
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @param refetchInterval - Optional polling interval in ms (e.g. 5000 for status polling)
 * @returns Query result with deployment data
 */
export function useGetDeployment(id: string, token?: string | null, refetchInterval?: number) {
  return useQuery({
    queryKey: ['deployments', id, !!token],
    queryFn: async () => {
      const client = createClient(DeploymentService, transport(token));
      const request = create(GetDeploymentRequestSchema, { id });
      return await client.getDeployment(request);
    },
    enabled: !!id && !!token,
    refetchInterval: refetchInterval ?? false,
  });
}

/**
 * Hook to list all available control planes.
 * Control planes change infrequently so a longer stale time is used.
 *
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @returns Query result with list of control planes
 */
export function useListControlPlanes(token?: string | null) {
  return useQuery({
    queryKey: ['control-planes', 'list', !!token],
    queryFn: async () => {
      const client = createClient(DeploymentService, transport(token));
      const request = create(ListControlPlanesRequestSchema, {});
      return await client.listControlPlanes(request);
    },
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to list the DuploCloud infrastructures ("plans") that exist in a
 * deployment's portal, queried live. Used to populate the infra selector on
 * workspace creation. Each returned name is a valid infra.
 *
 * @param deploymentId - the deployment whose portal infras to list
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @returns Query result with the deployment's infra list
 */
export function useListDeploymentInfras(deploymentId?: string, token?: string | null) {
  return useQuery({
    queryKey: ['deployment-infras', deploymentId, !!token],
    queryFn: async () => {
      const client = createClient(DeploymentService, transport(token));
      const request = create(ListDeploymentInfrasRequestSchema, {
        deploymentId: deploymentId ?? '',
      });
      return await client.listDeploymentInfras(request);
    },
    enabled: !!deploymentId && !!token,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to retrieve the rendered bootstrap template for a deployment.
 * The template is fetched from cloud storage via the backend, so the
 * UI never needs direct access to the S3 presigned URL.
 *
 * @param deploymentId - The deployment ID whose template to retrieve
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @param enabled - Whether the query should execute (default: true)
 * @returns Query result with the template content string
 */
export function useGetBootstrapTemplate(
  deploymentId: string,
  token?: string | null,
  enabled = true,
) {
  return useQuery({
    queryKey: ['deployments', deploymentId, 'bootstrap-template', !!token],
    queryFn: async () => {
      const client = createClient(DeploymentService, transport(token));
      const request = create(GetBootstrapTemplateRequestSchema, { deploymentId });
      return await client.getBootstrapTemplate(request);
    },
    enabled: !!deploymentId && !!token && enabled,
  });
}

/**
 * Mutation hook to delete a deployment.
 * Initiates deprovisioning and tombstoning of the deployment.
 * On success, invalidates the deployments list cache.
 *
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @returns Mutation result for deleting a deployment
 */
export function useDeleteDeployment(token?: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; mode?: DeleteMode }) => {
      const client = createClient(DeploymentService, transport(token));
      const request = create(DeleteDeploymentRequestSchema, {
        id: input.id,
        mode: input.mode ?? DeleteMode.FULL,
      });
      return await client.deleteDeployment(request);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['deployments', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['deployments', variables.id] });
    },
  });
}

export function useTriggerTenantMigration(token?: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (deploymentId: string) => {
      const client = createClient(DeploymentService, transport(token));
      return await client.triggerTenantMigration(
        create(TriggerTenantMigrationRequestSchema, { id: deploymentId }),
      );
    },
    onSuccess: (_data, deploymentId) => {
      queryClient.invalidateQueries({ queryKey: ['deployments', deploymentId] });
    },
  });
}

/**
 * Hook to check which resources block deleting a deployment.
 * Returns active workspaces that must be removed before the deployment can be deleted.
 *
 * @param deploymentId - The deployment ID to check
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @param enabled - Whether the query should execute (default: true)
 * @returns Query result with list of blocking resources
 */
export function useCheckDeploymentDeleteBlockers(
  deploymentId: string,
  token?: string | null,
  enabled = true,
) {
  return useQuery({
    queryKey: ['deployments', deploymentId, 'delete-blockers', !!token],
    queryFn: async () => {
      const client = createClient(DeploymentService, transport(token));
      const request = create(CheckDeploymentDeleteBlockersRequestSchema, {
        deploymentId,
      });
      return await client.checkDeploymentDeleteBlockers(request);
    },
    enabled: !!deploymentId && !!token && enabled,
  });
}

/**
 * Query hook to fetch the most recent deployment-scoped workflow run (e.g. a
 * bulk multi-workspace promote/upgrade) for a deployment's own "what last
 * happened at the deployment level" banner. Per-workspace runs are not
 * included — see useGetLastWorkspaceWorkflowRun for those, on the workspace page.
 *
 * staleTime: Infinity - purely event-driven, no time-based refetch.
 * useStreamDeploymentEvents already invalidates this query's cache key on
 * every deployment/audit/workflow_run event (any non-keepalive message,
 * including the snapshot sent right on connect), which bypasses staleTime
 * and forces a refetch - so a fresh dispatch, completion, or a
 * dropped-then-restored connection all refetch live without ever needing a
 * refetchInterval or refetch-on-focus/mount.
 *
 * @param deploymentId - the deployment to check
 * @param token - Auth0 access token (required for authenticated endpoints)
 */
export function useGetLastDeploymentWorkflowRun(deploymentId: string, token?: string | null) {
  return useQuery({
    queryKey: ['deployments', deploymentId, 'lastWorkflowRun', !!token],
    staleTime: Infinity,
    queryFn: async () => {
      const client = createClient(DeploymentService, transport(token));
      const request = create(GetLastDeploymentWorkflowRunRequestSchema, { deploymentId });
      return await client.getLastDeploymentWorkflowRun(request);
    },
    enabled: !!deploymentId && !!token,
  });
}

/**
 * Subscribes to the deployment SSE stream and invalidates React Query caches
 * when a status change or audit event fires for this deployment.
 * Reconnects automatically with 5-second backoff on connection loss.
 */
export function useStreamDeploymentEvents(
  deploymentId: string,
  accessToken: string | null | undefined,
) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!accessToken || !deploymentId) return;

    let cancelled = false;
    const abortController = new AbortController();

    async function listen() {
      while (!cancelled) {
        try {
          const t = createAuthApiTransport(accessToken!);
          const client = createClient(DeploymentService, t);
          const request = create(StreamDeploymentEventsRequestSchema, { deploymentId });

          for await (const event of client.streamDeploymentEvents(request, {
            signal: abortController.signal,
          })) {
            if (cancelled) return;
            try {
              const p = event.payload ? (JSON.parse(event.payload) as { event_type?: string }) : {};
              if (p.event_type === 'keepalive') continue;
            } catch {
              // malformed payload — treat as a change signal
            }
            queryClient.invalidateQueries({ queryKey: ['deployments', deploymentId] });
            queryClient.invalidateQueries({
              queryKey: ['audit', 'list', 'deployment', deploymentId],
            });
          }
        } catch (err) {
          if (cancelled || isNonRetriable(err)) return;
        }
        await streamSleep(5000, abortController.signal).catch(() => {});
      }
    }

    listen();
    return () => {
      cancelled = true;
      abortController.abort();
    };
  }, [deploymentId, accessToken, queryClient]);
}
